import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const categories = [
  { title: 'Social Content', slug: 'social-content' },
  { title: 'Beauty & Wellness', slug: 'beauty-wellness' },
  { title: 'Founder Brands', slug: 'founder-brands' },
  { title: 'Events & Experiences', slug: 'events-experiences' },
  { title: 'Editorial Campaigns', slug: 'editorial-campaigns' },
];

export default function PortfolioPage() {
  return (
    <div className="pt-44" data-testid="portfolio-page">
      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="portfolio-hero">
        <div className="container-main">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Selected Work</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-10">
              A curated collection of brands, campaigns, and visual stories.
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-4">
              Every project begins with intention. Our portfolio reflects brands we have helped shape through strategy, content, production, events, and creative direction.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              We believe strong visuals should not simply look beautiful. They should communicate identity. They should feel aligned. They should create recognition.
            </motion.p>

            <motion.div variants={fadeInUp} className="mt-12">
              <img src="/images/portfolio-hero-moodboard.png" alt="Curated moodboard" className="w-full max-w-lg mx-auto h-auto object-contain" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* Categories */}
      <section className="py-16 md:py-24" data-testid="portfolio-categories">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="space-y-0 divide-y divide-border">
            {categories.map((cat, i) => (
              <motion.div key={i} variants={fadeInUp} className="py-12 first:pt-0 last:pb-0" data-testid={`portfolio-cat-${i}`}>
                <Link to={`/portfolio/${cat.slug}`} className="flex items-center justify-between group">
                  <h2 className="font-serif text-2xl md:text-3xl font-medium group-hover:text-butter transition-colors duration-300">{cat.title}</h2>
                  <span className="text-xs tracking-widest uppercase text-muted-foreground group-hover:text-butter transition-colors duration-300 font-medium">View More</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-foreground" data-testid="portfolio-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-background mb-10">
              Interested In Creating Something Similar?
            </motion.h2>
            <motion.div variants={fadeInUp}>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="portfolio-cta-btn">
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
